import React, { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { dateTime } from "../../lib/format";
import { Button } from "../ui/button";

export interface GmailStatus { configured: boolean; connected: boolean; email?: string; connected_at?: string; last_sync_at?: string; needs_reconnect: boolean; can_send: boolean; messages: number; sync_days: number; unmatched_90d?: number; awaiting_reply?: number;
  sync: { running?: boolean; total?: number; fetched?: number; error?: string; last_mode?: string; last_count?: number } }

export function useGmailStatus() {
  return useQuery<GmailStatus>({ queryKey: ["gmail-status"], queryFn: () => api.get("/admin/gmail/status").then((r) => r.data), refetchInterval: (q) => (q.state.data?.sync?.running ? 3000 : 60000) });
}

export function GmailConnect() {
  const qc = useQueryClient();
  const { data } = useGmailStatus();
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("gmail");
    if (!r) return;
    if (r === "connected") toast.success("Gmail connected — pulling the last 12 months of mail in the background");
    else toast.error(`Gmail connection failed (${p.get("reason") || "unknown"})`);
    window.history.replaceState({}, "", window.location.pathname);
    qc.invalidateQueries({ queryKey: ["gmail-status"] });
  }, [qc]);
  const connect = async () => { try { const { data: d } = await api.get("/admin/gmail/connect"); window.location.href = d.authorization_url; } catch (e) { toast.error(errorMessage(e)); } };
  const sync = async (full = false) => { try { await api.post("/admin/gmail/sync", null, { params: full ? { full: true } : {} }); toast.success(full ? "Full re-sync started" : "Checking the mailbox for new mail…"); qc.invalidateQueries({ queryKey: ["gmail-status"] }); } catch (e) { toast.error(errorMessage(e)); } };
  const disconnect = async () => {
    if (!window.confirm("Disconnect the wineclub@ mailbox? Synced emails stay in the app until you reconnect and re-sync.")) return;
    try { await api.post("/admin/gmail/disconnect"); toast.success("Gmail disconnected"); qc.invalidateQueries({ queryKey: ["gmail-status"] }); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!data) return null;
  const s = data.sync || {};
  return (
    <div className={`mt-4 border p-5 ${data.connected && !data.needs_reconnect ? "border-[var(--moss)]" : "border-border"}`} data-testid="gmail-connect-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Mail className="mt-1 h-5 w-5" strokeWidth={1.5} />
          <div>
            <div className="eyebrow">Team mailbox · Gmail</div>
            <div className="mt-1 font-serif text-2xl" data-testid="gmail-state">
              {!data.configured ? "Awaiting Google keys" : !data.connected ? "Not connected" : data.needs_reconnect ? `Needs reconnecting · ${data.email}` : `Connected · ${data.email}`}
            </div>
            <p className="mt-1 max-w-xl text-sm text-neutral-600">
              {!data.configured ? "Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the backend to enable this." :
                !data.connected ? `Sign in once as the team mailbox. The last ${data.sync_days} days of sent and received mail are pulled in, then new mail every 10 minutes, and every email shows on the matching customer's sheet.` :
                  `${data.messages.toLocaleString()} emails synced${data.last_sync_at ? ` · last checked ${dateTime(data.last_sync_at)}` : ""}${data.can_send ? " · replies can be sent from the customer sheet" : " · read-only (reconnect to allow sending)"}`}
            </p>
            {s.running && <p className="mt-2 text-xs text-[var(--moss)]" data-testid="gmail-sync-progress">Syncing… {s.fetched ?? 0}{s.total ? ` of ${s.total}` : ""} emails</p>}
            {s.error && !s.running && <p className="mt-2 text-xs text-[var(--wine)]" data-testid="gmail-sync-error">Last sync failed: {s.error}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {data.configured && (!data.connected || data.needs_reconnect) && <Button className="rounded-none" onClick={connect} data-testid="gmail-connect-button">{data.needs_reconnect ? "Reconnect Gmail" : "Connect Gmail"}</Button>}
          {data.connected && !data.needs_reconnect && <>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => sync(false)} disabled={!!s.running} data-testid="gmail-sync-button">Check for new mail</Button>
            <Button variant="ghost" size="sm" className="rounded-none" onClick={() => sync(true)} disabled={!!s.running} data-testid="gmail-full-sync-button">Full re-sync</Button>
            <Button variant="ghost" size="sm" className="rounded-none text-[var(--wine)]" onClick={disconnect} data-testid="gmail-disconnect-button">Disconnect</Button>
          </>}
        </div>
      </div>
    </div>
  );
}
